import { createContext, useContext, useState, useCallback, type ReactNode } from 'react';

interface ModalEntry {
  id: string;
  title: ReactNode;
  render: ReactNode | (() => ReactNode);
  footer?: ReactNode;
  onClose?: () => void;
  pageScope?: string;
}

interface ModalContextValue {
  openModal: (opts: ModalEntry) => void;
  closeModal: (id?: string) => void;
  confirm: (message: string) => boolean;
}

const ModalContext = createContext<ModalContextValue>({
  openModal: () => {},
  closeModal: () => {},
  confirm: () => false,
});

export function useModal(): ModalContextValue {
  return useContext(ModalContext);
}

export function ModalProvider({ children }: { children: ReactNode }) {
  const [stack, setStack] = useState<ModalEntry[]>([]);

  const openModal = useCallback((opts: ModalEntry) => {
    setStack(prev => {
      const idx = prev.findIndex(e => e.id === opts.id);
      if (idx >= 0) { const next = [...prev]; next[idx] = opts; return next; }
      return [...prev, opts];
    });
  }, []);

  const closeModal = useCallback((id?: string) => {
    setStack(prev => {
      if (!id) return prev.slice(0, -1);
      return prev.filter(e => e.id !== id);
    });
  }, []);

  const confirm = useCallback((message: string) => window.confirm(message), []);

  const top = stack.length > 0 ? stack[stack.length - 1] : null;

  return (
    <ModalContext.Provider value={{ openModal, closeModal, confirm }}>
      {children}
      {top && (
        <>
          <div className="scrim mo-scrim is-on" onClick={() => { closeModal(top.id); top.onClose?.(); }} />
          <div className="modal mo is-on" role="dialog" aria-modal="true">
            <div className="mo-win">
              <div className="mo-h">
                <h3>{top.title}</h3>
                <button className="mo-x" onClick={() => { closeModal(top.id); top.onClose?.(); }} aria-label="Close">✕</button>
              </div>
              {top.pageScope ? (
                <div id={top.pageScope} className="mo-scope">
                  <div className="mo-b">
                    {typeof top.render === 'function' ? top.render() : top.render}
                  </div>
                  {top.footer && <div className="mo-f">{top.footer}</div>}
                </div>
              ) : (
                <>
                  <div className="mo-b">
                    {typeof top.render === 'function' ? top.render() : top.render}
                  </div>
                  {top.footer && <div className="mo-f">{top.footer}</div>}
                </>
              )}
            </div>
          </div>
        </>
      )}
    </ModalContext.Provider>
  );
}
